import { LanguageKeys } from "@/locales/types";
import { defineStore } from "pinia";
import Postmate from "postmate";

interface UserPermissions {
  contentAiEnabled: boolean;
}

interface FeaturePermissions {
  contentAI: boolean;
}

interface LabsFeatureFlags {
  location: Record<string, any>;
}

export interface AppStore {
  locationId: string;
  userEmail: string;
  userId: string;
  userName: string;
  token: string;
  appHandshake: Postmate.Model | undefined;
  requestRetryCount: number;
  companyId: string;
  timezone: string;
  appType: "STANDALONE" | "EMBED";
  userPermissions?: UserPermissions;
  featurePermissions?: FeaturePermissions;
  labsFeatureFlags: LabsFeatureFlags;
  locale: LanguageKeys;
  rootUrl: string;
  homeId: string;
  domain: string;
  userType: string;
  userRole: string;
  apiDomain: string | null;
  brandedDomain: string | null;
}

export const useAppStore = defineStore("app", {
  state: (): AppStore => ({
    locationId: (import.meta.env.VITE_LOCATION_ID as string) || "",
    userId: (import.meta.env.VITE_USER_ID as string) || "",
    userEmail: (import.meta.env.VITE_USER_EMAIL as string) || "",
    userName: (import.meta.env.VITE_USER_NAME as string) || "",
    token: (import.meta.env.VITE_TOKEN_ID as string) || "",
    appType: import.meta.env.VITE_APP_TYPE || "STANDALONE",
    requestRetryCount: 0,
    companyId: "",
    timezone: "",
    appHandshake: undefined,
    userPermissions: {
      contentAiEnabled: false,
    },
    featurePermissions: {
      contentAI: false,
    },
    labsFeatureFlags: {
      location: {},
    },
    locale: LanguageKeys.EnglishUS,
    rootUrl: "",
    homeId: "",
    domain: "",
    userType: "",
    userRole: "",
    apiDomain: null,
    brandedDomain: null,
  }),
  getters: {
    isBlogAiEnabled: (state): boolean => {
      return state.labsFeatureFlags.location?.blog?.blogAi === true;
    },
  },
  actions: {
    async fetchLabsFeatureFlags() {
      try {
        const { LabsService } = await import("@/services/LabsService");
        const { data } = await LabsService.getFeatureFlags(this.locationId);
        console.log("data", data);
        this.labsFeatureFlags = {
          location: data.featureFlags?.location || {},
        };
      } catch (error) {
        console.error("[AppStore] Failed to fetch labs feature flags:", error);
        this.labsFeatureFlags = { location: {} };
      }
    },
    initialise(payload: AppStore) {
      console.log("initialising app store", payload);
      this.locationId = payload.locationId;
      this.userId = payload.userId;
      this.userEmail = payload.userEmail;
      this.userName = payload.userName || "";
      this.token = payload.token;
      this.companyId = payload.companyId;
      this.timezone = payload.timezone;
      this.appType = payload.appType;
      this.userPermissions = payload.userPermissions;
      this.featurePermissions = payload.featurePermissions;
      this.locale = payload.locale;
      this.rootUrl = payload.rootUrl;
      this.userType = payload.userType;
      this.userRole = payload.userRole;
      this.apiDomain = payload.apiDomain ?? null;
      this.brandedDomain = payload.brandedDomain ?? null;
    },
    refreshToken(newToken: string) {
      this.token = newToken;
    },
    setLocationId(locationId: string) {
      this.locationId = locationId;
    },
    setAppHandshake(payload: any) {
      this.appHandshake = payload;
    },
    updateRequestRetryCount(count: number) {
      this.requestRetryCount = count;
    },
    isfeatureActive() {
      const now = new Date();
      const levelupDate = new Date("2025-10-13T00:00:00-04:00");
      const excemptedAccounts = [
        "ktapXmiRvMM4pJHyN3b3",
        "YuTUZlUtrwBtvmgByZDW",
        "5DP4iH6HLkQsiKESj6rh", // Staging
      ];
      if (this.companyId) {
        if (now >= levelupDate || excemptedAccounts.indexOf(this.companyId) > -1) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    },
  },
});
